import React, { useState, useEffect } from 'react';
import { Calendar, X, ChevronRight } from 'lucide-react';

const CalendarOfEvents = () => {
    const [annualCalendars, setAnnualCalendars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const ANNUAL_API = 'https://admin.torchbearer.co.ke/api/annual-calendars';
    const BASE_URL = 'https://admin.torchbearer.co.ke';

    useEffect(() => {
        fetchAnnualCalendars();
    }, []);

    const fetchAnnualCalendars = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(ANNUAL_API);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            setAnnualCalendars(result.data || []);
        } catch (error) {
            console.error('Error fetching calendars:', error);
            setError('Failed to load the annual training calendars. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#fff8d9] pt-32 pb-20 md:pt-48 md:pb-32">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d97707] mx-auto mb-4"></div>
                    <p className="text-lg text-[#d97707] font-medium">Loading calendars...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fff8d9] pt-32 pb-20 md:pt-48 md:pb-32">
            {/* Hero Section */}
            <div className="relative py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-extrabold text-[#d97707] mb-6">
                        Annual Training Calendars
                    </h1>
                    <p className="text-xl md:text-2xl text-[#ceb699] max-w-3xl mx-auto">
                        View and download our comprehensive training schedules for the entire year.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                {error ? (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md shadow-sm">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <X className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                                <button
                                    onClick={fetchAnnualCalendars}
                                    className="mt-2 text-sm font-medium text-red-700 hover:text-red-600 underline"
                                >
                                    Try again
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {annualCalendars.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-amber-100">
                                <Calendar className="w-16 h-16 text-amber-200 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">No annual calendars found</h3>
                                <p className="text-gray-500 max-w-md mx-auto">
                                    We are currently updating our annual training schedules. Please check back later or contact us for more information.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {annualCalendars.map((cal) => (
                                    <div key={cal.id} className="bg-white p-6 rounded-2xl shadow-sm border border-amber-100 hover:shadow-md transition-shadow flex items-center justify-between group">
                                        <div className="flex items-center">
                                            <div className="p-3 bg-amber-50 rounded-xl mr-4 group-hover:bg-amber-100 transition-colors">
                                                <Calendar className="w-6 h-6 text-[#d97707]" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900 uppercase tracking-tight">{cal.title}</h3>
                                                <p className="text-sm text-gray-500">Year: {cal.year}</p>
                                            </div>
                                        </div>
                                        <a
                                            href={`${BASE_URL}${cal.file_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-3 bg-[#d97707] text-white rounded-xl hover:bg-amber-700 transition-all flex items-center gap-2 font-medium"
                                        >
                                            Download
                                            <ChevronRight className="w-4 h-4" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CalendarOfEvents;
